<template>
  <v-card id="create-agent-rule">
    <v-dialog
      v-if="confirmationDialog"
      v-model="confirmationDialog"
      width="400px"
      @click:outside="confirmationDialog = false"
      @keydown.esc="confirmationDialog = false"
    >
      <i-confirmation-dialog
        :text="`Invite this Agent to place Turn In Orders for ${isILR ? 'Customers' : 'Retailers'} in selected States.`"
        action-text="Confirm"
        @cancel="confirmationDialog = false"
        @confirm="addAgentInvitation()"
      />
    </v-dialog>

    <v-card-title class="i-card-title-div justify-center py-4">
      <span class="g-bold g-heading-m">Invite Agent</span>
    </v-card-title>

    <v-card-text class="i-card-content-div mt-6 mb-4">
      <p class="g-bold g-heading-s g-text-color-black">Agent Details</p>
      <v-alert
        dense
        outlined
        text
        border="left"
        class="mb-2"
        elevation="1"
        type="info"
      >
        <span class="g-bold">Note: Email Address is required to Login</span>
      </v-alert>

      <v-row class="mt-3">
        <v-col class="py-0" cols="12" md="6">
          <v-text-field
            outlined
            required
            background-color="#0000000f"
            dense
            label="Email Address"
            v-model="agentEmail"
            :error-messages="emailValidation"
            :loading="checkingEmail"
            :rules="rulesEmail"
          ></v-text-field>
        </v-col>
        <v-col class="py-0" cols="12" md="6">
          <v-text-field
            outlined
            required
            background-color="#0000000f"
            dense
            label="Confirm Email Address"
            :loading="checkingEmail"
            :rules="[emailConfirmation]"
            :value="reAgentEmail"
            @input="(email) => checkAgentEmailDebounce(email)"
          ></v-text-field>
        </v-col>
      </v-row>

      <v-alert
        v-if="!(isRegisteredAgent || isNormalUser || isNewAgent)"
        dense
        outlined
        text
        border="left"
        class="mb-2"
        elevation="1"
        type="info"
      >
        <span class="g-bold"
          >Note: If Agent is already registered in the platform, invite will be
          sent automatically. If Agent is new further details will be
          required.</span
        >
      </v-alert>

      <v-alert
        v-if="isRegisteredAgent"
        dense
        outlined
        text
        border="left"
        class="mb-2"
        elevation="1"
        type="success"
      >
        <span class="g-bold">
          Agent is registered in the Platform.<br />
          Please select State(s) the Agent can access.<br />
          Ensure {{ isILR ? 'Customers' : 'Retailers' }} are approved in the
          Agent's set State(s) before sending invite.<br />
          Agent will be provided access to approved
          {{ isILR ? 'Customers' : 'Retailers' }} tomorrow, or the day after any
          new {{ isILR ? 'Customer(S)' : 'Retailer(S)' }} are approved in the
          Agents set State(s).
        </span>
      </v-alert>

      <v-alert
        v-if="isNormalUser"
        dense
        outlined
        text
        border="left"
        class="mb-2"
        elevation="1"
        type="error"
      >
        <span class="g-bold"
          >Email is associated to an existing standard user. Rule cannot be
          created.</span
        >
      </v-alert>

      <v-alert
        v-if="isNewAgent"
        dense
        outlined
        text
        border="left"
        class="mb-2"
        elevation="1"
        type="info"
      >
        <span class="g-bold">
          Agent is not registered. Provide Agent details & select States Agent
          can access.<br />
          Ensure {{ isILR ? 'Customers' : 'Retailers' }} are approved in the
          Agent's set State(s) before sending invite.<br />
          Agent will only receive an email invite the day after at least one
          {{ isILR ? 'Customer' : 'Retailer' }} in the Agent's set State(s) has
          approved Agent access.
        </span>
      </v-alert>

      <div v-if="isNewAgent">
        <v-form v-model="formAgentDetails" @submit.prevent>
          <v-row class="mt-6">
            <v-col class="py-0" cols="12" md="6">
              <v-text-field
                outlined
                required
                background-color="#F0F0F0"
                dense
                label="* First Name (required)"
                v-model="agentFirstName"
                :rules="rulesFirstName"
              ></v-text-field>
            </v-col>
            <v-col class="py-0" cols="12" md="6">
              <v-text-field
                outlined
                required
                background-color="#F0F0F0"
                dense
                label="* Last Name (required)"
                v-model="agentLastName"
                :rules="rulesLastName"
              ></v-text-field>
            </v-col>

            <v-col class="py-0" cols="12" md="6">
              <v-text-field
                outlined
                required
                background-color="#F0F0F0"
                dense
                label="* Mobile or Landline (required)"
                hint="Add Area Code if Landline"
                v-model="agentMobile"
                :rules="rulesPhone"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-form>
      </div>

      <div v-if="!isNormalUser" class="mt-6">
        <p class="g-bold g-heading-s g-text-color-black">State(s)</p>

        <v-autocomplete
          attach
          background-color="#0000000f"
          class="mt-3 mb-6"
          clearable
          deletable-chips
          dense
          hide-details
          :items="stateOptions"
          label="Select State(s)"
          multiple
          outlined
          :rules="[(v) => !!v.length || 'State(s) required for State rebates']"
          v-model:search-input="searchInput"
          small-chips
          v-model="targetStoreDeliveryAddressStates"
          @change="handleStatesChange"
        ></v-autocomplete>
      </div>
    </v-card-text>

    <v-divider></v-divider>

    <v-card-actions class="i-dialog-actions-div justify-end">
      <v-btn rounded text class="i-btn-text-black" @click="handleCancel">
        <strong>Cancel</strong>
      </v-btn>

      <v-btn
        rounded
        class="i-btn-block-secondary mr-4"
        @click="handleCreate"
        :disabled="!validRule"
        :loading="loading"
      >
        <strong>Create</strong>
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import { createAgentSupplierRule } from '@/services/agencyRules'
import { checkAgentRegistration } from '@/services/userAccess'
import { addAgentInvitation } from '@/services/invitation'
import FieldValidators from '@/utils/FieldValidators'
import iConfirmationDialog from '@/components/ConfirmationDialog'
import { buildStateOptions } from '@/utils/stateDisplay'

const DEBOUNCE_TIMER = 1000

const EMAIL_VALIDATION_RESULTS = {
  EXISTS: 'EXISTS',
  NOT_EXISTS: 'NOT_EXISTS',
  INVITATION_PENDING: 'INVITATION_PENDING',
  NOT_AN_AGENT: 'NOT_AN_AGENT'
}
const ADD_ALL_ZONES_OPTION = 'Add all zones'

export default {
  components: {
    iConfirmationDialog
  },

  data: () => ({
    agentEmail: null,
    reAgentEmail: null,
    agentFirstName: null,
    agentLastName: null,
    agentMobile: null,
    agentRole: 'AGENT',
    confirmationDialog: false,
    checkingEmail: false,
    debounceEmail: null,
    emailValidation: null,
    emailValidationResult: null,
    formAgentDetails: false,
    loading: false,
    searchInput: '',
    targetStoreDeliveryAddressStates: [],
    ...FieldValidators
  }),

  computed: {
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapGetters('tenant', ['isILR', 'tenantCountryRegion']),
    states() {
      return buildStateOptions(this.tenantCountryRegion).map(
        (stateOption) => stateOption.value
      )
    },
    stateOptions() {
      return [
        ADD_ALL_ZONES_OPTION,
        ...buildStateOptions(this.tenantCountryRegion).map((stateOption) => ({
          text: stateOption.text,
          value: stateOption.value
        }))
      ]
    },

    emailConfirmation() {
      return this.doEmailsMatch || 'Emails must match!'
    },

    doEmailsMatch() {
      return this.agentEmail === this.reAgentEmail
    },

    isValidEmail() {
      return !!this.agentEmail && /.+@.+/.test(this.agentEmail)
    },

    isRegisteredAgent() {
      return [
        EMAIL_VALIDATION_RESULTS.EXISTS,
        EMAIL_VALIDATION_RESULTS.INVITATION_PENDING
      ].includes(this.emailValidationResult)
    },

    isNewAgent() {
      return this.emailValidationResult === EMAIL_VALIDATION_RESULTS.NOT_EXISTS
    },

    isNormalUser() {
      // Normal means any registered that is NOT an agent
      return (
        this.emailValidationResult === EMAIL_VALIDATION_RESULTS.NOT_AN_AGENT
      )
    },

    hasSelectedStates() {
      return this.targetStoreDeliveryAddressStates?.length > 0
    },

    hasValidAgentDetails() {
      if (!this.isNewAgent) return true
      else return this.formAgentDetails
    },

    validRule() {
      return (
        this.isValidEmail &&
        this.doEmailsMatch &&
        this.emailValidationResult &&
        this.hasValidAgentDetails &&
        !this.isNormalUser &&
        this.hasSelectedStates
      )
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    handleStatesChange(selectedStates = []) {
      if (selectedStates.includes(ADD_ALL_ZONES_OPTION)) {
        this.targetStoreDeliveryAddressStates = [...this.states]
      } else {
        this.targetStoreDeliveryAddressStates = selectedStates.filter(
          (state) => state !== ADD_ALL_ZONES_OPTION
        )
      }
      this.searchInput = ''
    },

    checkAgentEmailDebounce(email) {
      this.reAgentEmail = email
      if (!this.reAgentEmail || !this.doEmailsMatch) return

      // Only validate in backend once has passed frontend validations
      if (this.isValidEmail) {
        clearTimeout(this.debounceEmail)
        this.debounceEmail = setTimeout(() => {
          this.checkingEmail = true
          this.emailValidation = `Validating Agent's email`

          checkAgentRegistration(email)
            .then((res) => {
              this.emailValidationResult = res
            })
            .catch((err) => {
              // Validation APIs have request limiters
              const errMessage =
                err.response.status === 429
                  ? 'We detected too many Email field validation attempts. Please wait a few minutes before trying again.'
                  : 'Email validation not completed, try again...'

              this.addNotification({
                color: 'error',
                text: errMessage,
                timeout: -1
              })
            })
            .finally(() => {
              this.checkingEmail = false
              this.emailValidation = null
            })
        }, DEBOUNCE_TIMER)
      }
    },

    handleCancel() {
      this.agentEmail = null
      this.$emit('cancel')
    },

    handleCreate() {
      if (this.isNewAgent) this.confirmationDialog = true
      else this.createAgentSupplierRule()
    },

    addAgentInvitation() {
      this.confirmationDialog = false
      this.loading = true

      const payload = {
        invitedEmail: this.agentEmail,
        invitedFirstName: this.agentFirstName,
        invitedLastName: this.agentLastName,
        invitedMobile: this.agentMobile,
        invitedRole: this.agentRole
      }

      addAgentInvitation(payload)
        .then(() => {
          this.createAgentSupplierRule()
        })
        .catch(() => {
          this.loading = false
          this.addNotification({
            color: 'error',
            text: 'Agent invitation could not be created, try again...'
          })
        })
    },

    createAgentSupplierRule() {
      this.loading = true

      const payload = {
        supplierId: this.selectedSupplierId,
        updateData: {
          agentEmail: this.agentEmail,
          targetStoreDeliveryAddressStates:
            this.targetStoreDeliveryAddressStates
        }
      }

      createAgentSupplierRule(payload)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Agent Supplier Rule created!'
          })
          this.agentEmail = null
          this.$emit('confirm')
        })
        .catch((err) => {
          let message =
            'Agent Supplier Rule could not be created, please try again...'

          if (
            err.response?.data?.code ===
            'AGENT_SUPPLIER_AGENT_EMAIL_ALREADY_EXISTS'
          ) {
            message =
              'A rule already exists for this agent, update it if required.'
          }

          this.addNotification({
            color: 'error',
            text: message
          })
        })
        .finally(() => {
          this.loading = false
        })
    }
  },

  watch: {
    agentEmail() {
      this.checkingEmail = false
      this.reAgentEmail = null
      this.emailValidationResult = null
    }
  }
}
</script>

<style lang="scss" scoped>
#create-agent-rule {
  display: flex !important;
  flex-direction: column;
  overflow: hidden;
  box-shadow: none;
  background: $bg-secondary-color;

  .v-card__text {
    flex-grow: 1;
    overflow: auto;
  }
}
</style>
